<template>
    <v-container fluid v-if="sessionActive" class="pa-3">
        <material-header title="Panelist manager" info="Editing panelist" icon="mdi-account-group" :loading="profileLoading">

            <template slot="buttons">

                <v-tooltip :color="hasPermission(minPermission) ? 'primary' : 'red'" top>
                    <template v-slot:activator="{ on }">
                        <v-btn :disabled="!hasPermission(minPermission)"
                               @click="refreshClicked()"
                               v-on="on"
                               icon>
                            <v-icon>mdi-refresh</v-icon>
                        </v-btn>
                    </template>
                    <span v-if="hasPermission(minPermission)">Reload panelist</span>
                    <span v-else>You are not authorized to fetch data for panelists</span>
                </v-tooltip>

                <v-tooltip top color="primary">
                    <template v-slot:activator="{ on }">
                        <v-btn v-on="on" fab small class="ml-4 elevation-2" color="red" dark @click="closeClicked">
                            <v-icon> mdi-close</v-icon>
                        </v-btn>
                    </template>
                    <span>Close and go to back to search results</span>
                </v-tooltip>

            </template>
        </material-header>

        <v-card v-if="!hasPermission('view panelists')">
            <v-card-text>
                <v-alert
                    class="body-2"
                    type="error"
                >
                    Your user account doesn't have sufficient permissions for this feature
                </v-alert>
            </v-card-text>
        </v-card>

        <div v-if="hasPermission('view panelists')">

            <v-card flat>
                <v-card-text class="pa-0">
                    <v-tabs v-if="hasPermission('view panelists')"
                            v-model="activeTab"
                            dark
                            slider-color="primary"
                            color="white"
                            show-arrows
                    >
                        <v-tab class="ml-4" key="profile">Profile</v-tab>
                        <v-tab key="timeline">Timeline</v-tab>
                        <v-tab key="rewards" v-show="!isExternallySupplied">Rewards</v-tab>
                        <v-tab key="redemptions" v-show="!isExternallySupplied">Redemptions</v-tab>
                        <v-tab key="variables" v-show="!isExternallySupplied">Variables</v-tab>
                        <v-tab key="activity">Activity</v-tab>

                        <!-- PROFILE -->
                        <v-tab-item key="profile" reverse-transition="fade-transition"
                                    transition="fade-transition">
                            <profile
                                :data="profile"
                                @delete="deletePanelist"
                            />
                        </v-tab-item>

                        <!-- TIMELINE -->
                        <v-tab-item key="timeline" reverse-transition="fade-transition" transition="fade-transition">
                            <time-line :data="profile"/>
                        </v-tab-item>

                        <!-- REWARDS -->
                        <v-tab-item v-show="!isExternallySupplied" key="rewards" reverse-transition="fade-transition"
                                    transition="fade-transition">
                            <rewards :data="profile"/>
                        </v-tab-item>

                        <!-- REDEMPTIONS -->
                        <v-tab-item v-show="!isExternallySupplied" key="redemptions" reverse-transition="fade-transition"
                                    transition="fade-transition">
                            <redemptions :data="profile"/>
                        </v-tab-item>

                        <!-- VARIABLES -->
                        <v-tab-item v-show="!isExternallySupplied" key="variables" reverse-transition="fade-transition"
                                    transition="fade-transition">
                            <variables
                                v-if="profile.data_variables"
                                :data="profile"
                            />
                        </v-tab-item>

                        <!-- ACTIVITY -->
                        <v-tab-item key="activity" reverse-transition="fade-transition"
                                    transition="fade-transition">
                            <activity :items="profile.activity_history"/>
                        </v-tab-item>

                    </v-tabs>
                </v-card-text>
            </v-card>

        </div>

        <!-- DEBUG -->
        <v-card v-if="debug" color="blue-grey darken-4">
            <v-card-title>
                <v-icon large class="mr-2">mdi-bug</v-icon>
                <h3>Debug</h3>
            </v-card-title>
            <v-card-text>
                <json :data="profile"></json>
            </v-card-text>
        </v-card>

    </v-container>
</template>

<script>

import Store from '../store';
import activeSessionChecker from '../../../components/mixins/activeSessionCheckerMixin';
import MaterialHeader from '../../../components/UI/MaterialHeader';
import {mapState, mapMutations, mapGetters, mapActions} from 'vuex';
import Json from 'vue-json-pretty';

import Profile from './Tabs/Profile';
import TimeLine from './Tabs/TimeLine';
import Rewards from './Tabs/Rewards';
import Redemptions from "./Tabs/Redemptions";
import Variables from './Tabs/Variables';
import Activity from './Tabs/Activity';

export default {

    mixins: [activeSessionChecker],

    components: {
        MaterialHeader,
        Json,
        Profile,
        TimeLine,
        Rewards,
        Variables,
        Redemptions,
        Activity,
    },

    data() {
        return {
            debug: false,
            confirmationDialogModel: false,
            minPermission: 'view panelists',
            activeTab: null,
        }
    },

    watch: {
        // Route changed
        '$route'() {
            // In-bound route only
            if (this.$route.name === 'panelist-manager-edit') {
                this.loadPanelist();
            }
        }
    },

    created() {

        if (!this.$store.state.PanelistManager) {
            this.$store.registerModule('PanelistManager', Store);
        }

        if (this.$route.params.id) {
            this.loadPanelist();
        } else {
            // New panelist
        }

        this.loadSubPanels();
        this.loadRecruitmentSources();
    },

    computed: {

        ...mapState('PanelistManager', [
            'profile',
            'profileLoading',
        ]),

        warningsExist() {
            if (!this.hasPermission('update panelists')) return true;
            if (!this.hasPermission('view pi')) return true;
        },

        warningsText() {

            let warnings = [];

            if (!this.hasPermission('update panelists')) {
                warnings.push("update panelists");
            }
            if (!this.hasPermission('view pi')) {
                warnings.push("view Personally Identifiable (PI) data variables");
            }

            return "Your account does not have permissions to " + warnings.join(' and ');
        },

        isExternallySupplied() {
            return this.profile.external_supplier_id;
        }
    },

    methods: {

        ...mapMutations('PanelistManager', [
            'clearEditState'
        ]),

        ...mapActions('RecruitmentSources', [
            'loadRecruitmentSources',
        ]),

        ...mapActions('SubPanels', [
            'loadSubPanels',
        ]),

        ...mapActions('PanelistManager', [
            'loadProfile',
            'deleteProfile',
        ]),

        loadPanelist() {
            // Do not reload panelist if already loaded
            if (this.profile.panelist_id !== this.$route.params.id) {
                this.clearEditState();
                this.getProfile(this.$route.params.id);
            }
        },

        getProfile(id, showReloadMessage = false) {
            this.loadProfile(id)
                .then(() => {
                    if (showReloadMessage) {
                        Bus.$emit('flash-message', {
                            message: 'Panelist profile reloaded!'
                        });
                    }
                })
                .catch(() => {
                    Bus.$emit('flash-message', {
                        type: 'error',
                        message: 'Failed loading panelist profile!'
                    });
                });
        },

        deletePanelist() {
            this.deleteProfile(this.profile.panelist_id)
                .then(() => {
                    this.$router.go(-1);
                    Bus.$emit('flash-message', {
                        message: 'Panelist deleted!'
                    });
                })
                .catch((error) => {

                    if (error.response.status === 404) {
                        Bus.$emit('flash-message', {
                            type: 'error',
                            message: 'This panelist is no longer found!'
                        });
                    } else {
                        Bus.$emit('flash-message', {
                            type: 'error',
                            message: 'Failed to delete the panelist!'
                        });
                    }
                });
        },

        saveClicked() {
            Bus.$emit('flash-message', {message: 'Sorry this feature is not available yet'});
        },

        refreshClicked() {
            this.getProfile(this.$route.params.id, true);
        },

        closeClicked() {
            this.$router.go(-1);
        }
    }
}
</script>
